import React, { useEffect, useState } from 'react';
import {
  Button,
  Field,
  Input,
  makeStyles,
  shorthands,
  MessageBar,
  MessageBarBody,
} from '@fluentui/react-components';
import { ArrowSync20Regular } from '@fluentui/react-icons';
import { BaseDialog } from './BaseDialog';
import { StaffUser } from '../services/userService';

const useStyles = makeStyles({
  passwordRow: {
    display: 'flex',
    alignItems: 'flex-end',
    ...shorthands.gap('8px'),
  },
});

function generateTempPassword(): string {
  const alphabet = 'ABCDEFGHJKMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789';
  const bytes = new Uint32Array(12);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => alphabet[b % alphabet.length]).join('');
}

interface ResetPasswordDialogProps {
  isOpen: boolean;
  user: StaffUser | null;
  saving?: boolean;
  error?: string | null;
  onClose: () => void;
  onSubmit: (password: string) => void;
}

export const ResetPasswordDialog: React.FC<ResetPasswordDialogProps> = ({
  isOpen,
  user,
  saving,
  error,
  onClose,
  onSubmit,
}) => {
  const styles = useStyles();
  const [password, setPassword] = useState('');

  useEffect(() => {
    if (isOpen) setPassword(generateTempPassword());
  }, [isOpen]);

  const valid = password.length >= 8;

  return (
    <BaseDialog
      isOpen={isOpen}
      onClose={onClose}
      title={`Reset password${user ? ` — ${user.name || user.email}` : ''}`}
      maxWidth="480px"
      showCloseButton
      actions={
        <>
          <Button appearance="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            appearance="primary"
            onClick={() => valid && onSubmit(password)}
            disabled={!valid || saving}
          >
            {saving ? 'Saving…' : 'Set password'}
          </Button>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        {error && (
          <MessageBar intent="error">
            <MessageBarBody>{error}</MessageBarBody>
          </MessageBar>
        )}
        <Field
          label="Temporary password"
          required
          validationState={password && !valid ? 'error' : 'none'}
          validationMessage={password && !valid ? 'Must be at least 8 characters' : undefined}
          hint="Share this with the user — they can change it from their profile after signing in."
        >
          <div className={styles.passwordRow}>
            <Input
              size="large"
              style={{ flex: 1 }}
              value={password}
              onChange={(_, d) => setPassword(d.value)}
            />
            <Button
              size="large"
              icon={<ArrowSync20Regular />}
              onClick={() => setPassword(generateTempPassword())}
              title="Generate a new password"
            >
              Generate
            </Button>
          </div>
        </Field>
      </div>
    </BaseDialog>
  );
};

export default ResetPasswordDialog;
